if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initMarketingWidgets();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      captureViewportState() {
  return {
    scrollX: window.scrollX || window.pageXOffset || 0,
    scrollY: window.scrollY || window.pageYOffset || 0,
  };
}

restoreViewportState(viewportState) {
  if (!viewportState) return;

  const restore = () => {
    window.scrollTo(viewportState.scrollX, viewportState.scrollY);
  };

  restore();

  window.requestAnimationFrame(() => {
    restore();
    window.requestAnimationFrame(restore);
  });
}


      initMarketingWidgets() {
        this.clearMarketingTimers();
        this.initUrgencyCounters();
        this.initDemoDownloadButtons();
      }

      clearMarketingTimers() {
        if (this.marketingIntervalIds?.length) {
          this.marketingIntervalIds.forEach((intervalId) => window.clearInterval(intervalId));
        }
        this.marketingIntervalIds = [];

        if (this.marketingVisibilityHandlers?.length) {
          this.marketingVisibilityHandlers.forEach((handler) =>
            document.removeEventListener('visibilitychange', handler)
          );
        }
        this.marketingVisibilityHandlers = [];
      }

      initUrgencyCounters() {
        const counters = this.querySelectorAll('[data-pdp-urgency-counter]');
        if (!counters.length) return;

        counters.forEach((counter) => {
          if (counter.dataset.initialized === 'true') return;
          counter.dataset.initialized = 'true';

          const valueElement = counter.querySelector('[data-pdp-urgency-counter-value]');
          if (!valueElement) return;

          const storageKey = counter.dataset.storageKey || `bn-pdp-urgency:${this.dataset.productId || this.dataset.url}`;
          const initialCount = this.parseMarketingInteger(valueElement.textContent, this.parseMarketingInteger(counter.dataset.baseCount, 0));
          const bootstrapMin = this.parseMarketingInteger(counter.dataset.bootstrapMin, 150);
          const bootstrapMax = this.parseMarketingInteger(counter.dataset.bootstrapMax, 200);
          const stepMin = this.parseMarketingInteger(counter.dataset.stepMin, 300);
          const stepMax = this.parseMarketingInteger(counter.dataset.stepMax, 500);
          const refreshInterval = this.parseMarketingInteger(counter.dataset.refreshInterval, 300000);
          const locale = document.documentElement.lang || 'es-CL';

          const updateCounter = (allowIncrement = false) => {
            const now = Date.now();
            const storedState = this.readUrgencyState(storageKey);
            let nextValue = initialCount;
            let lastUpdate = now;

            if (!storedState) {
              nextValue = initialCount + this.randomBetween(bootstrapMin, bootstrapMax);
            } else {
              nextValue = storedState.value;
              lastUpdate = storedState.lastUpdate;

              if (allowIncrement && !document.hidden && now - storedState.lastUpdate >= refreshInterval) {
                nextValue = storedState.value + this.randomBetween(stepMin, stepMax);
                lastUpdate = now;
              }
            }

            valueElement.textContent = new Intl.NumberFormat(locale).format(nextValue);
            this.writeUrgencyState(storageKey, { value: nextValue, lastUpdate });
          };

          updateCounter(false);

          const intervalId = window.setInterval(() => {
            updateCounter(true);
          }, Math.min(refreshInterval, 60000));

          const visibilityHandler = () => {
            if (!document.hidden) updateCounter(true);
          };

          document.addEventListener('visibilitychange', visibilityHandler);
          this.marketingIntervalIds.push(intervalId);
          this.marketingVisibilityHandlers.push(visibilityHandler);
        });
      }

      initDemoDownloadButtons() {
        this.querySelectorAll('[data-demo-download-button]').forEach((button) => {
          if (button.dataset.initialized === 'true') return;
          button.dataset.initialized = 'true';

          button.addEventListener('click', () => {
            const demoUrl = button.dataset.demoDownloadUrl;
            if (!demoUrl) return;

            const downloadWindow = window.open(demoUrl, '_blank', 'noopener,noreferrer');
            if (downloadWindow) downloadWindow.opener = null;
          });
        });
      }

      parseMarketingInteger(value, fallback) {
        const normalizedValue = `${value ?? ''}`.replace(/[^\d-]/g, '');
        const parsedValue = Number.parseInt(normalizedValue, 10);
        return Number.isFinite(parsedValue) ? parsedValue : fallback;
      }

      randomBetween(min, max) {
        const floor = Math.min(min, max);
        const ceiling = Math.max(min, max);
        return Math.floor(Math.random() * (ceiling - floor + 1)) + floor;
      }

      readUrgencyState(storageKey) {
        try {
          const rawState = window.localStorage.getItem(storageKey);
          if (!rawState) return null;

          const parsedState = JSON.parse(rawState);
          if (!parsedState || !Number.isFinite(parsedState.value) || !Number.isFinite(parsedState.lastUpdate)) {
            return null;
          }

          return parsedState;
        } catch (error) {
          return null;
        }
      }

      writeUrgencyState(storageKey, state) {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(state));
        } catch (error) {
          /* noop */
        }
      }


      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.clearMarketingTimers();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
  if (!this.contains(event.target)) return;

  this.resetProductFormState();

  const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
  this.pendingRequestUrl = productUrl;
  const shouldSwapProduct = this.dataset.url !== productUrl;
  const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
  const shouldPreserveViewport = !shouldSwapProduct;

  this.renderProductInfo({
    requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
    targetId: target.id,
    callback: shouldSwapProduct
      ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
      : this.handleUpdateProductInfo(productUrl),
    viewportState: shouldPreserveViewport ? this.captureViewportState() : null,
  });
}

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, viewportState = null }) {
  this.abortController?.abort();
  this.abortController = new AbortController();

  fetch(requestUrl, { signal: this.abortController.signal })
    .then((response) => response.text())
    .then((responseText) => {
      this.pendingRequestUrl = null;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      callback(html);
    })
    .then(() => {
      this.restoreViewportState(viewportState);

      const optionToFocus = document.querySelector(`#${targetId}`);
      if (optionToFocus) {
        try {
          optionToFocus.focus({ preventScroll: true });
        } catch (error) {
          optionToFocus.focus();
          this.restoreViewportState(viewportState);
        }
      }
    })
    .catch((error) => {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted by user');
      } else {
        console.error(error);
      }
    });
}

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (!source || !destination) return;

            destination.innerHTML = source.innerHTML;

            if (id === 'Inventory') {
              this.syncInventoryStateAttributes(source, destination);
            }

            destination.classList.toggle('hidden', shouldHide(source));
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // Kondasoft
          updateSourceFromDestination('ks-product-quantity-breaks')
          // /Kondasoft

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      syncInventoryStateAttributes(source, destination) {
        const shouldSyncAttribute = (attributeName) =>
          attributeName === 'data-product-id' ||
          attributeName === 'data-selected-variant-id' ||
          attributeName === 'data-maximum-inventory' ||
          attributeName === 'data-low-stock-threshold' ||
          attributeName === 'data-variant-available' ||
          attributeName.startsWith('data-inventory-');

        Array.from(destination.attributes).forEach(({ name }) => {
          if (shouldSyncAttribute(name) && !source.hasAttribute(name)) {
            destination.removeAttribute(name);
          }
        });

        Array.from(source.attributes).forEach(({ name, value }) => {
          if (shouldSyncAttribute(name)) {
            destination.setAttribute(name, value);
          }
        });

        ['role', 'aria-live', 'aria-atomic'].forEach((attributeName) => {
          if (source.hasAttribute(attributeName)) {
            destination.setAttribute(attributeName, source.getAttribute(attributeName));
          } else {
            destination.removeAttribute(attributeName);
          }
        });

        destination.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
        destination.toggleAttribute('hidden', source.hasAttribute('hidden'));
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}